import { TFunction, useTranslation } from 'react-i18next';
import styles from './CreateLeaderboardPage.module.less';
import {
	Col,
	Row,
	Form,
	Button,
	Radio,
	Select,
	Space,
	RadioChangeEvent,
	DatePicker,
	Spin,
} from 'antd';
import FormInput from 'components/form/FormItem/FormInput';
import { useHistory, useParams } from 'react-router-dom';
import api, {
	CreateLeaderboardRequest,
	LeaderboardSortOrder,
	LeaderboardResetFrequency,
	GameStatDtoPagedResult,
} from 'api';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactNode, useEffect, useState } from 'react';
import OmniNotification from 'components/OmniNotification';
import { useSelector } from 'react-redux';
import { projectSelector } from '../../../redux/selectors/projectSelector';
import Breadcrumb, { IRoutes } from '../../../components/Breadcrumb/Breadcrumb';
import FormItem from 'components/form/FormItem';
import moment from 'moment';
import { isDate, isString } from 'lodash';

const { Option } = Select;

type ParamsProps = {
	projectId: string;
};

type Props = {
	leaderboard?: CreateLeaderboardRequest;
	loading?: boolean;
	children?: ReactNode;
};

const defaultValues: CreateLeaderboardRequest = {
	title: '',
	apiName: '',
	sortOrder: LeaderboardSortOrder.HighestIsLeader,
	resetFrequency: LeaderboardResetFrequency.Daily,
	resetHour: undefined,
};

var projectId = 0;

const getValidationSchema = (
	t: TFunction,
	productId: string,
	existingApiName: string | undefined,
) =>
	yup.object().shape({
		apiName: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('createLeaderboardPage.apiName.label'),
				}),
			)
			.test(
				'valid-apiName-free',
				({ value }) =>
					t('createLeaderboardPage.apiName.alreadyTaken', { name: value }),
				async (value, context) => {
					if (!existingApiName || existingApiName !== value)
						return api.validApiName2({
							apiName: value,
							productId,
						});
					return true;
				},
			)
			.matches(
				/^(?!.*[?;\/:@=&]).*$/,
				t('common.unsafeCharacters', {
					fieldName: t('createLeaderboardPage.apiName.label'),
				}),
			)
			.matches(
				/^[\S]*$/,
				t('common.noSpaces', {
					fieldName: t('createLeaderboardPage.apiName.label'),
				}),
			)
			.max(
				1000,
				t('validation.maxLength', {
					fieldName: t('createLeaderboardPage.apiName.label'),
					count: 1000,
				}),
			),
		title: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('createLeaderboardPage.title.label'),
				}),
			)
			.max(
				80,
				t('validation.maxLength', {
					fieldName: t('createLeaderboardPage.title.label'),
					count: 80,
				}),
			),
		gameStatId: yup.string().required(
			t('validation.required', {
				fieldName: t('createLeaderboardPage.gameStat.label'),
			}),
		),
		resetHour: yup
			.date()
			.nullable()
			.when('resetFrequency', {
				is: (value: any) => value != LeaderboardResetFrequency.Never,
				then: yup
					.date()
					.nullable()
					.required(
						t('validation.required', {
							fieldName: t('createLeaderboardPage.resetHour.label'),
						}),
					),
			}),
	});

export default function CreateLeaderboardPage(props: Props | undefined) {
	const { t } = useTranslation();
	const history = useHistory();
	const projectInfo = useSelector(projectSelector);
	const validationSchema: any = getValidationSchema(
		t,
		projectInfo?.productId as string,
		props?.leaderboard?.apiName,
	);
	const [sortOrder, setSortOrder] = useState(0);
	const [resetFrequency, setResetFrequency] = useState(0);
	const [routes, setRoutes] = useState<IRoutes[]>([]);
	const [gameStats, setGameStats] = useState<GameStatDtoPagedResult>({
		items: [],
		totalCount: 0,
	});
	const [resetHourVisible, setResetHourVisible] = useState(false);

	projectId = Number.parseInt(useParams<ParamsProps>().projectId);

	const pageForm = useForm<CreateLeaderboardRequest>({
		mode: 'onChange',
		resolver: yupResolver(validationSchema),
		defaultValues,
	});

	useEffect(() => {
		if (props?.leaderboard) {
			pageForm.reset(props?.leaderboard);
			setSortOrder(props?.leaderboard?.sortOrder!);
			setResetFrequency(props?.leaderboard?.resetFrequency!);
		}
	}, [props?.leaderboard]);

	useEffect(() => {
		const noNeedResetHour = resetFrequency == LeaderboardResetFrequency.Never;

		setResetHourVisible(!noNeedResetHour);
		if (noNeedResetHour) {
			pageForm.setValue('resetHour', undefined);
		}
	}, [resetFrequency]);

	useEffect(() => {
		loadGameStats();
	}, [projectInfo?.productId]);

	useEffect(() => {
		setRoutes([
			{
				path: `project/${projectInfo?.id}`,
				breadcrumbName: projectInfo?.name as string,
			},
			{
				path: `game-services`,
				breadcrumbName: t('gameServicesPage.header'),
			},
			{
				path: 'leaderboards',
				breadcrumbName: t('leaderboardsPage.header'),
			},
			{
				path: '',
				breadcrumbName: props?.leaderboard
					? t('leaderboardsPage.edit')
					: t('createLeaderboardPage.header'),
			},
		]);
	}, [projectInfo?.id, projectInfo?.name, props?.leaderboard]);

	const navigateToLeaderboardsIndex = () => {
		history.push(`/project/${projectId}/game-services/leaderboards`);
	};

	const loadGameStats = async () => {
		if (projectInfo?.productId === '') {
			return;
		}
		var gameStats = await api.getGameStats(
			projectInfo?.productId as string,
			0,
			1000,
		);
		setGameStats(gameStats);
	};

	const onSortOrderChange = (e: RadioChangeEvent) => {
		pageForm.setValue(
			'sortOrder',
			Number.parseInt(e.target.value) as LeaderboardSortOrder,
		);
		setSortOrder(e.target.value);
	};

	const onResetFrequencyChange = (e: RadioChangeEvent) => {
		pageForm.setValue(
			'resetFrequency',
			Number.parseInt(e.target.value) as LeaderboardResetFrequency,
		);
		setResetFrequency(e.target.value);
	};

	const onSubmit = async (values) => {
		try {
			values.productId = projectInfo?.productId;
			if (props?.leaderboard?.apiName) {
				await api.updateLeaderboardDefinition(
					projectInfo?.productId!,
					props.leaderboard.apiName,
					{
						...values,
					},
				);
				OmniNotification.success({
					message: t('createLeaderboardPage.notification.update.message'),
					description: t(
						'createLeaderboardPage.notification.update.description',
						{
							title: values.title,
						},
					),
				});
			} else {
				values.productId = projectInfo?.productId;
				await api.createLeaderboard(values);
				OmniNotification.success({
					message: t('createLeaderboardPage.notification.create.message'),
					description: t(
						'createLeaderboardPage.notification.create.description',
						{
							title: values.title,
						},
					),
				});
			}
		} catch {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: t('api.defaultError'),
			});
		}

		navigateToLeaderboardsIndex();
	};

	return (
		<Spin spinning={props?.loading || false}>
			<div className={styles.pageContainer}>
				<Breadcrumb routes={routes} />
				<div className="page-head">
					<div>
						<h2 className="h2" style={{ wordBreak: 'break-all' }}>
							{props?.leaderboard
								? t('createLeaderboardPage.editHeader')
								: t('createLeaderboardPage.header')}
						</h2>
					</div>
				</div>
				<FormProvider {...pageForm}>
					<Form
						data-testid="leaderboardForm"
						layout="vertical"
						onFinish={pageForm.handleSubmit(onSubmit)}
					>
						<Row gutter={16}>
							<Col span={16}>
								<Row gutter={24}>
									<Col span={12} data-testid="apiNameGroup">
										<FormInput
											control={pageForm.control}
											translationKey="createLeaderboardPage.apiName"
										/>
									</Col>
									<Col span={12} data-testid="titleGroup">
										<FormInput
											control={pageForm.control}
											translationKey="createLeaderboardPage.title"
										/>
									</Col>
								</Row>
							</Col>
						</Row>
						<Row gutter={16} className={styles.formRow}>
							<Col span={16} data-testid="gameStatSelectGroup">
								<Row gutter={24}>
									<Col span={12}>
										<FormItem
											control={pageForm.control}
											label={
												<div>
													<div>{t('createLeaderboardPage.gameStat.label')}</div>
													<div className="ant-form-item-explain hint">
														{t('createLeaderboardPage.gameStat.hint')}
													</div>
												</div>
											}
											name="gameStatId"
											render={({ field: { onChange, value } }) => {
												return (
													<Select
														data-testid="gameStatSelect"
														value={value}
														placeholder={t(
															'createLeaderboardPage.form.api_name.placeholder',
														)}
														onChange={onChange}
													>
														{gameStats.items?.map((gameStat) => (
															<Option
																data-testid={`option-${gameStat.apiName}`}
																key={gameStat.id}
																value={gameStat.id!}
															>
																{gameStat.apiName}
															</Option>
														))}
													</Select>
												);
											}}
										/>
									</Col>
								</Row>
							</Col>
						</Row>
						<Row gutter={24} className={styles.formRow}>
							<Col span={24}>
								<Row>{t('createLeaderboardPage.sortOrder.label')}</Row>
								<Row className="ant-form-item-explain hint">
									{t('createLeaderboardPage.sortOrder.hint')}
								</Row>
							</Col>
						</Row>
						<Row>
							<Col span={24} style={{ marginLeft: 20, marginTop: 4 }}>
								<Radio.Group
									name="sortOrder"
									onChange={onSortOrderChange}
									value={sortOrder}
								>
									<Space direction="vertical">
										<Radio
											data-testid="leaderboardSortOrderLowest"
											value={LeaderboardSortOrder.LowestIsLeader}
										>
											{t(
												`createLeaderboardPage.sortOrder.sortOrderEnum.lowestFirst`,
											)}
										</Radio>
										<Radio
											data-testid="leaderboardSortOrderHighest"
											value={LeaderboardSortOrder.HighestIsLeader}
										>
											{t(
												`createLeaderboardPage.sortOrder.sortOrderEnum.highestFirst`,
											)}
										</Radio>
									</Space>
								</Radio.Group>
							</Col>
						</Row>
						<Row gutter={24} className={styles.formRow}>
							<Col span={24}>
								<Row>{t('createLeaderboardPage.resetFrequency.label')}</Row>
								<Row className="ant-form-item-explain hint">
									{t('createLeaderboardPage.resetFrequency.hint')}
								</Row>
							</Col>
						</Row>
						<Row>
							<Col span={24} style={{ marginLeft: 20, marginTop: 4 }}>
								<Radio.Group
									name="resetFrequency"
									onChange={onResetFrequencyChange}
									value={resetFrequency}
								>
									<Space direction="vertical">
										<Radio
											data-testid="leaderboardResetFrequencyDaily"
											value={LeaderboardResetFrequency.Daily}
										>
											{t(
												`createLeaderboardPage.resetFrequency.resetFrequencyEnum.daily`,
											)}
										</Radio>
										<Radio
											data-testid="leaderboardResetFrequencyWeekly"
											value={LeaderboardResetFrequency.Weekly}
										>
											{t(
												`createLeaderboardPage.resetFrequency.resetFrequencyEnum.weekly`,
											)}
										</Radio>
										<Radio
											data-testid="leaderboardResetFrequencyMonthly"
											value={LeaderboardResetFrequency.Monthly}
										>
											{t(
												`createLeaderboardPage.resetFrequency.resetFrequencyEnum.monthly`,
											)}
										</Radio>

										<Radio
											data-testid="leaderboardResetFrequencyYearly"
											value={LeaderboardResetFrequency.Yearly}
										>
											{t(
												`createLeaderboardPage.resetFrequency.resetFrequencyEnum.yearly`,
											)}
										</Radio>
										<Radio
											data-testid="leaderboardResetFrequencyNever"
											value={LeaderboardResetFrequency.Never}
										>
											{t(
												`createLeaderboardPage.resetFrequency.resetFrequencyEnum.never`,
											)}
										</Radio>
									</Space>
								</Radio.Group>
							</Col>
						</Row>
						{resetHourVisible && (
							<Row gutter={24} className={styles.formRow}>
								<Col data-testid="resetHourPickerFormItem">
									<FormItem
										control={pageForm.control}
										label={
											<div>
												<div>{t('createLeaderboardPage.resetHour.label')}</div>
												<div className="ant-form-item-explain hint">
													{t('createLeaderboardPage.resetHour.hint')}
												</div>
											</div>
										}
										name="resetHour"
										render={({ field: { onChange, value } }) => {
											const resetHour = isString(value)
												? moment
														.utc(
															(value as string).substring(0, 19),
															'YYYY-MM-DDTHH:mm:ss',
														)
														.local()
												: isDate(value)
												? moment(value).local()
												: value;
											return (
												<DatePicker
													data-testid="resetHourPicker"
													picker="time"
													onChange={onChange}
													value={resetHour}
												/>
											);
										}}
									/>
								</Col>
							</Row>
						)}
						<Row gutter={24} style={{ marginTop: 24 }}>
							<Button
								className="ant-btn color-dark"
								data-testid="cancel"
								onClick={navigateToLeaderboardsIndex}
							>
								{t('common.cancel')}
							</Button>
							<Button
								className="ant-btn-primary"
								data-testid="submit"
								style={{ marginLeft: '8px' }}
								htmlType="submit"
							>
								{props?.leaderboard
									? t('common.SaveChanges')
									: t('createLeaderboardPage.create')}
							</Button>
						</Row>
					</Form>
				</FormProvider>
			</div>
		</Spin>
	);
}
